'use client';

import { useState } from 'react';

interface CarLoanState {
  loanAmount: number;
  interestRate: number;
  loanTerm: number;
}

const INITIAL_STATE: CarLoanState = {
  loanAmount: 30000,
  interestRate: 5.5,
  loanTerm: 60,
};

const PRESETS: { [key: string]: CarLoanState } = {
  conservative: {
    loanAmount: 20000,
    interestRate: 4.5,
    loanTerm: 48,
  },
  moderate: {
    loanAmount: 30000,
    interestRate: 5.5,
    loanTerm: 60,
  },
  aggressive: {
    loanAmount: 45000,
    interestRate: 6.5,
    loanTerm: 72,
  },
};

export default function CalculatorCarLoan() {
  const [inputs, setInputs] = useState<CarLoanState>(INITIAL_STATE);
  const [isCalculating, setIsCalculating] = useState(false);

  const calculate = () => {
    const monthlyRate = inputs.interestRate / 100 / 12;
    const monthlyPayment =
      inputs.loanAmount *
      (monthlyRate * Math.pow(1 + monthlyRate, inputs.loanTerm)) /
      (Math.pow(1 + monthlyRate, inputs.loanTerm) - 1);
    const totalPaid = monthlyPayment * inputs.loanTerm;
    const totalInterest = totalPaid - inputs.loanAmount;

    return {
      monthlyPayment,
      totalPaid,
      totalInterest,
    };
  };

  const handleInputChange = (field: keyof CarLoanState, value: number) => {
    setInputs({ ...inputs, [field]: value });
    setIsCalculating(true);
    setTimeout(() => {
      setIsCalculating(false);
    }, 800);
  };

  const results = calculate();

  const handleReset = () => {
    setInputs(INITIAL_STATE);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    setInputs(PRESETS[preset]);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="space-y-6 mb-8">
          <InputField
            label="Loan Amount"
            value={inputs.loanAmount}
            onChange={(v) => handleInputChange('loanAmount', v)}
            min={1000}
            prefix="$"
          />
          <InputField
            label="Interest Rate (APR)"
            value={inputs.interestRate}
            onChange={(v) => handleInputChange('interestRate', v)}
            min={0}
            max={30}
            step={0.1}
            unit="%"
          />
          <InputField
            label="Loan Term"
            value={inputs.loanTerm}
            onChange={(v) => handleInputChange('loanTerm', v)}
            min={12}
            max={84}
            unit="months"
          />
        </div>

        <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 rounded-xl p-8 text-white text-center">
          <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">
            Monthly Payment
          </div>
          <div className="text-5xl font-bold mb-2">
            ${results.monthlyPayment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-sm opacity-90">
            For {inputs.loanTerm} months
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="bg-slate-50 rounded-lg p-4 text-center">
            <div className="text-xs text-slate-600 mb-1">Total Interest</div>
            <div className="text-xl font-bold text-slate-900">
              ${results.totalInterest.toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 text-center">
            <div className="text-xs text-slate-600 mb-1">Total Paid</div>
            <div className="text-xl font-bold text-slate-900">
              ${results.totalPaid.toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InputField({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  prefix,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  unit?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          step={step}
          className={`w-full ${prefix ? 'pl-7' : 'pl-3'} ${unit ? 'pr-14' : 'pr-3'} py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900`}
        />
        {unit && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}